package com.neurobs.ccabserver.administrator.view

import com.neurobs.ccab.{AccountSubjectResponse, SubjectCreateData}
import com.neurobs.ccabserver.administrator.controller.SubjectListController
import com.neurobs.js.dom.{ElementFactory, ForceSelectBox, Grid, HideableRowGrid, InputRadio}
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.Grid._
import com.neurobs.js.view.{Form, Gui}
import com.neurobs.js._
import com.neurobs.js.ccab.CCABServer
import scalatags.JsDom.all._
import scala.math.{min, max}

import scala.concurrent.{Future, Promise}

class SubjectSelectionView {
	import SubjectSelectionView._

	private val boxWidth = 20

	private val subjectSelectElement = ForceSelectBox(makeSelectBox(width = boxWidth))
	def subjectSelect: view.SelectBox = subjectSelectElement

	private val retrievingMessage = span("Retrieving subjects...").render
	def setRetrieveError(message: String): Unit = {
		retrievingMessage.innerHTML = message
	}

	val createPanel = new CreateSubjectPanel

	private val existingRadioElement = InputRadio(makeRadio(inputName = "SubjectSelectionViewType", inputValue = "existing", checked = false))
	def existingRadio: view.Radio = existingRadioElement

	private val newRadioElement = InputRadio(makeRadio(inputName = "SubjectSelectionViewType", inputValue = "new", checked = true))
	def newRadio: view.Radio = newRadioElement

	private val grid = {
		val temp = new HideableRowGrid(fullWidth = false, emGapSize = 1, defaultAlign = Grid.alignCenter)
		temp.addRow(span(existingRadioElement.input, " Existing subject", div(style := "width:2em;display:inline-block;"), newRadioElement.input, " New subject").render)
		temp.addRow(subjectSelectElement.select)
		temp.setRowVisible(subjectSelectElement.select, visible = false)
		temp.addRow(retrievingMessage)
		temp.setRowVisible(retrievingMessage, visible = false)
		temp.addRow(toElement(createPanel.panel))
		temp
	}

	def makeForm(): view.Form = ElementFactory.makeForm(width = 40, title = "Subject Selection", description = "", elements = Array(), useCancel = true, submitCaption = "Create", extraContent = Some(grid.container))

	def setDisplay(t: DisplayType.Value): Unit = {
		grid.setRowVisible(subjectSelectElement.select, visible = (t == DisplayType.List))
		grid.setRowVisible(retrievingMessage, visible = (t == DisplayType.GetList))
		grid.setRowVisible(toElement(createPanel.panel), visible = (t == DisplayType.New))
	}

}

object SubjectSelectionView {
	object DisplayType extends Enumeration {
		val List, GetList, New = Value
	}

	def selectSubject(accountId: Long, dataPassword: String, gui: Gui): Future[Option[AccountSubjectResponse]] = {
		val promise = Promise[Option[AccountSubjectResponse]]

		val view = new SubjectSelectionView
		val form = view.makeForm()
		var subjectList: Option[Array[AccountSubjectResponse]] = None

		def populateSelect(): Unit = {
			val list = subjectList.get
			view.subjectSelect.setOptions(list.map(_.identity.map(_.summary).getOrElse("Unidentified")))
			view.subjectSelect.setSize(max(3, min(20, list.length + 2)))
		}
		view.existingRadio.setSelectHandler { () =>
			form.submitButton.setCaption("Select")
			if (subjectList.isDefined) view.setDisplay(DisplayType.List)
			else {
				view.setDisplay(DisplayType.GetList)
				CCABServer.getSubjects(accountId, dataPassword) {
					case Left(error) => view.setRetrieveError(error.message)
					case Right(obj) => obj.data match {
						case None => view.setRetrieveError(obj.error.getOrElse("Error retrieving subjects."))
						case Some(list) =>
							subjectList = Some(list.sortWith{ (s1, s2) =>
								val res = for (i1 <- s1.identity; i2 <- s2.identity) yield i1 < i2
								res.getOrElse(false)

							}.toArray)
							populateSelect()
							if (view.existingRadio.checked) {
								view.setDisplay(DisplayType.List)
							}
					}
				}
			}
		}
		view.newRadio.setSelectHandler { () =>
			form.submitButton.setCaption("Create")
			view.setDisplay(DisplayType.New)
		}

		def process(callback: (Option[String]) => Unit): Either[String, Form.Process] = {
			if (view.existingRadio.checked) {
				view.subjectSelect.selectedIndex match {
					case None => Left("Please select a subject.")
					case Some(i) =>
						subjectList match {
							case None => Left("The subject list was not available.")
							case Some(list) =>
								callback(None)
								promise.success(Some(list(i)))
								Right(() => Unit)
						}
				}
			}
			else {
				SubjectListController.validateCreateData(view.createPanel) match {
					case Left(error) => Left(error)
					case Right(id) =>
						val data = SubjectCreateData(data = id, dataPassword = dataPassword, reportingId = None)
						Right(CCABServer.createSubject(accountId = accountId, data = data)(CCABServer.objectResultHandler(callback) { (subject) =>
								promise.success(subject)
						}))
				}
			}
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => promise.success(None)))

		promise.future
	}
}
